// covered-by: frontends/carrie/src/services/__tests__/askPuntt.test.ts
// covered-by: qa/rickrubin/tests/api/ask-puntt-share.spec.ts
import {
  type AskPunttChatScope,
  type AskPunttMessage,
  type GetAskPunttChatHistoryResponse,
  type ListAskPunttChatsResponse,
  type SendAskPunttMessageResponse,
} from "@mg/schemas/src/christo/askPuntt";
import {
  type GetAskPunttProjectResponse,
  type ListAskPunttProjectsResponse,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useUI } from "../../contexts/ui";
import * as AskPunttService from "../../services/askPuntt";
import { switchEnterprise } from "../../services/auth";
import { useAppDispatch, useAppSelector } from "../hooks";
import { login } from "../slices/auth";

/**
 * React Query hooks for Ask Puntt chat API
 */

// Query keys for cache management. The `chatsPrefix` / `allChatsPrefix`
// keys are intended for broad invalidations (`invalidateQueries({ queryKey:
// askPunttKeys.chatsPrefix })` invalidates "mine", "shared", and "all"
// scopes together because TanStack Query does prefix matching).
export const askPunttKeys = {
  all: ["ask-puntt"] as const,
  chatsPrefix: ["ask-puntt", "chats"] as const,
  allChatsPrefix: ["ask-puntt", "all-chats"] as const,
  chats: (scope?: AskPunttChatScope) =>
    [...askPunttKeys.all, "chats", scope ?? "mine"] as const,
  allChats: (scope?: AskPunttChatScope) =>
    [...askPunttKeys.all, "all-chats", scope ?? "mine"] as const,
  chat: (chatId: string) => [...askPunttKeys.all, "chat", chatId] as const,
  messages: (chatId: string) =>
    [...askPunttKeys.chat(chatId), "messages"] as const,
  streaming: (chatId: string, messageId: string) =>
    [...askPunttKeys.chat(chatId), "streaming", messageId] as const,
  userSearch: (q: string) => [...askPunttKeys.all, "user-search", q] as const,
} as const;

/**
 * Poll interval for chat/project list queries (sidebar recents, /chats,
 * /campaigns). Keeps `hasUnread` in sync when a review or document finishes
 * in a chat the user is not actively viewing.
 */
export const ASK_PUNTT_UNREAD_LIST_POLL_MS = 30_000;

/** Invalidate cached chat + project lists (sidebar unread dots). */
export function invalidateAskPunttUnreadLists(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
  queryClient.invalidateQueries({ queryKey: askPunttKeys.allChatsPrefix });
  queryClient.invalidateQueries({ queryKey: ["ask-puntt-projects", "list"] });
}

/** Append POST /messages rows to cached history without duplicating ids. */
export function appendSendResultToChatHistory(
  history: GetAskPunttChatHistoryResponse,
  result: SendAskPunttMessageResponse,
): GetAskPunttChatHistoryResponse {
  const ids = new Set(history.messages.map((m) => m._id));
  const messages = [...history.messages];
  const toAppend: AskPunttMessage[] = [result.userMessage];
  if (result.assistantMessage != null) {
    toAppend.push(result.assistantMessage);
  }
  for (const msg of toAppend) {
    if (!ids.has(msg._id)) {
      messages.push(msg);
      ids.add(msg._id);
    }
  }
  return { ...history, messages };
}

/**
 * List recent Ask Puntt chats for sidebar.
 *
 * `scope` defaults to "mine" (chats owned by the requester). Pass "shared"
 * for the "Shared with me" sidebar group, or "all" for a unified list with
 * `role: "owner" | "shared"` per row.
 */
export function useAskPunttChats(options?: {
  enabled?: boolean;
  scope?: AskPunttChatScope;
  refetchInterval?: number | false;
}) {
  const scope = options?.scope ?? "mine";
  return useQuery({
    queryKey: askPunttKeys.chats(scope),
    queryFn: () => AskPunttService.listChats({ limit: 20, scope }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * List all Ask Puntt chats (owned + shared-with-me; MG admins see enterprise).
 */
export function useAskPunttAllChats(options?: {
  enabled?: boolean;
  scope?: AskPunttChatScope;
  refetchInterval?: number | false;
}) {
  const scope = options?.scope ?? "all";
  return useQuery({
    queryKey: askPunttKeys.allChats(scope),
    queryFn: () => AskPunttService.listChats({ limit: 200, scope }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Get full chat history for a specific chat.
 * Pass refetchInterval to enable polling (e.g. while waiting for an AI response).
 *
 * `refetchInterval` accepts either a static number/false or a function that
 * receives the current Query so the caller can branch on the latest fetched
 * data (e.g. continue polling while the chat document is in the limbo window
 * between `reviewChecklist` being saved and `reviewStatus` being set).
 *
 * Auto-switches enterprise: if the chat is not found in the current enterprise
 * (404), automatically looks up the correct enterprise, switches to it, and
 * retries loading the chat.
 */
export function useAskPunttChatHistory(
  chatId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<GetAskPunttChatHistoryResponse, Error>,
        ) => number | false | undefined);
  },
) {
  const dispatch = useAppDispatch();
  const currentEnterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );

  return useQuery({
    queryKey: askPunttKeys.messages(chatId || ""),
    queryFn: async () => {
      try {
        return await AskPunttService.getChatHistory(chatId!);
      } catch (error: unknown) {
        // Check if this is a 404 error - chat not found in current enterprise
        const is404 =
          error instanceof Error &&
          "status" in error &&
          (error as { status?: number }).status === 404;

        if (is404 && chatId) {
          try {
            // Look up which enterprise owns this chat
            const { enterpriseId } =
              await AskPunttService.getChatEnterpriseId(chatId);

            // Only switch if it's a different enterprise
            if (enterpriseId !== currentEnterpriseId) {
              // Switch to the correct enterprise
              const { token } = await switchEnterprise(enterpriseId);
              dispatch(login({ token }));

              // Reload the page to refresh all data with the new enterprise context
              window.location.reload();

              // Return a pending promise to prevent further query execution
              // (page reload will handle the retry)
              return new Promise<GetAskPunttChatHistoryResponse>(() => {
                /* pending forever, reload handles it */
              });
            }
          } catch (_switchError) {
            // If enterprise lookup/switch fails, re-throw the original 404
            // (chat truly doesn't exist or user doesn't have access)
          }
        }

        // Re-throw original error if not a 404 or switch failed
        throw error;
      }
    },
    enabled: !!chatId && options?.enabled !== false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
    retry: false, // Disable automatic retries since we handle enterprise switching manually
  });
}

/**
 * Create a new Ask Puntt chat (sends first message and gets response)
 */
export function useCreateAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: AskPunttService.createChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to create Ask Puntt chat:", error);
      notify({
        title: "Failed to create chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Delete an Ask Puntt chat and invalidate the chats list.
 */
export function useDeleteAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (chatId: string) => AskPunttService.deleteChat(chatId),
    onSuccess: (_data, chatId) => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
      queryClient.removeQueries({ queryKey: askPunttKeys.chat(chatId) });
    },
    onError: (error) => {
      console.error("Failed to delete Ask Puntt chat:", error);
      notify({
        title: "Failed to delete chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Rename an Ask Puntt chat (owner-only). Optimistically updates the chat title
 * in BOTH the chat-history cache (drives the breadcrumb at the top of the chat
 * view) AND every cached chats list (drives the sidebar + chats page), then
 * invalidates so the server's canonical title wins. Reverts every touched
 * cache and toasts on error.
 */
export function useRenameAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      AskPunttService.renameChat(chatId, title),
    onMutate: async ({ chatId, title }) => {
      const trimmed = title.trim();
      const historyKey = askPunttKeys.messages(chatId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: historyKey }),
        queryClient.cancelQueries({ queryKey: askPunttKeys.chatsPrefix }),
        queryClient.cancelQueries({ queryKey: askPunttKeys.allChatsPrefix }),
      ]);

      const previousHistory =
        queryClient.getQueryData<GetAskPunttChatHistoryResponse>(historyKey);
      const previousLists =
        queryClient.getQueriesData<ListAskPunttChatsResponse>({
          queryKey: askPunttKeys.all,
        });

      queryClient.setQueryData<GetAskPunttChatHistoryResponse>(
        historyKey,
        (old) =>
          old ? { ...old, chat: { ...old.chat, title: trimmed } } : old,
      );

      // Update every cached chats list (mine / shared / all scopes) so the
      // sidebar row and chats page reflect the new title immediately.
      for (const [key, value] of previousLists) {
        if (!value?.chats) continue;
        queryClient.setQueryData<ListAskPunttChatsResponse>(key, {
          ...value,
          chats: value.chats.map((c) =>
            c._id === chatId ? { ...c, title: trimmed } : c,
          ),
        });
      }

      return { previousHistory, previousLists, historyKey };
    },
    onError: (error, _vars, context) => {
      if (context?.previousHistory !== undefined) {
        queryClient.setQueryData(context.historyKey, context.previousHistory);
      }
      for (const [key, value] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, value);
      }
      console.error("Failed to rename Ask Puntt chat:", error);
      notify({
        title: "Failed to rename chat",
        message: "Your change was reverted. Please try again.",
        variant: "error",
      });
    },
    onSettled: (_data, _error, { chatId }) => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
  });
}

/**
 * Lightweight polling for a single message's status fields.
 * Returns only streaming-relevant fields via MongoDB projection.
 */
export function useAskPunttMessageStatus(
  chatId: string | null,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: [...askPunttKeys.chat(chatId || ""), "message-status", messageId],
    queryFn: () => AskPunttService.getMessageStatus(chatId!, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Submit thumbs up/down feedback on an assistant message.
 * Optimistically updates the message in the query cache.
 */
export function useSubmitAskPunttFeedback(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      messageId: string;
      feedback: "thumbs_up" | "thumbs_down";
    }) =>
      AskPunttService.submitFeedback(chatId, params.messageId, params.feedback),
    onMutate: async (params) => {
      const queryKey = askPunttKeys.messages(chatId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === params.messageId
                ? { ...m, feedback: params.feedback }
                : m,
            ),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          askPunttKeys.messages(chatId),
          context.previous,
        );
      }
    },
  });
}

/**
 * Send a message to an existing Ask Puntt chat.
 * Returns the saved user message and a workflowId (AI response arrives async).
 */
export function useSendAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (payload: {
      message: string;
      attachments?: {
        key: string;
        source: string;
        type: string;
        name?: string;
      }[];
      sectionContext?: { id: string; title: string };
      mentions?: string[];
      mentionEmails?: string[];
      reviewMode?: "standard" | "deep";
    }) => AskPunttService.sendMessage(chatId, payload),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => (old != null ? appendSendResultToChatHistory(old, data) : old),
      );
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.chat(chatId),
      });
    },
    onError: (error) => {
      console.error("Failed to send Ask Puntt message:", error);
      notify({
        title: "Failed to send message",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Abort an in-progress Ask Puntt message (stop generating).
 * Optimistically clears waitingForAI state; caller handles revert on error.
 */
export function useAbortAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) =>
      AskPunttService.abortMessage(chatId, messageId),
    onSuccess: (data) => {
      // Apply the final (partial) message to the cache so polling stops naturally
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === data.message._id
                ? { ...m, ...data.message, status: "completed" as const }
                : m,
            ),
          };
        },
      );
    },
  });
}

/**
 * Lightweight streaming state poll for a single message.
 * Returns only the fields needed during streaming (no full chat document).
 * Uses 500ms polling interval for faster streaming updates.
 */
export function useAskPunttStreamingState(
  chatId: string,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttKeys.streaming(chatId, messageId || ""),
    queryFn: () => AskPunttService.getMessageStreamingState(chatId, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
    // `refetchInterval` IS the retry here — this query re-runs every 500ms on
    // its own. The global QueryClient sets no `retry`, so react-query's default
    // of 3 applied and every failing tick fanned out to 4 requests. /stream
    // 404s for the whole window before the turn's first snapshot flush, so that
    // turned an expected-and-transient failure into a request storm.
    retry: false,
  });
}

/**
 * Share an Ask Puntt chat with one or more enterprise users (owner-only).
 * On success invalidates the chat history cache so the share dialog and
 * header attribution re-render with the updated allowList, and the
 * "Shared with me" listings on recipients' sessions stay current after a
 * 30s poll.
 */
export function useShareAskPunttChat(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (payload: { userIds?: string[]; emails?: string[] }) =>
      AskPunttService.shareChat(chatId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to share Ask Puntt chat:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Remove a user from an Ask Puntt chat's allowList (owner-only).
 */
export function useUnshareAskPunttChat(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (userId: string) => AskPunttService.unshareChat(chatId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.allChatsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to unshare Ask Puntt chat:", error);
      notify({
        title: "Failed to remove user",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Server-side debounced enterprise user search. Caller controls the
 * debounce by gating `q` (e.g. via a 250ms debounced state value).
 */
export function useSearchEnterpriseUsers(
  q: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const trimmed = (q ?? "").trim();
  return useQuery({
    queryKey: askPunttKeys.userSearch(trimmed),
    queryFn: () =>
      AskPunttService.searchEnterpriseUsers(trimmed, options?.limit),
    enabled: options?.enabled !== false,
    // Bare `@` loads the roster — refetch whenever the menu reopens.
    staleTime: trimmed.length === 0 ? 0 : 30 * 1000,
    gcTime: 60 * 1000,
  });
}

function clearChatUnreadInLists(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
) {
  const listQueries = queryClient.getQueriesData<ListAskPunttChatsResponse>({
    queryKey: askPunttKeys.all,
  });
  for (const [key, value] of listQueries) {
    if (!value?.chats) continue;
    queryClient.setQueryData<ListAskPunttChatsResponse>(key, {
      ...value,
      chats: value.chats.map((c) =>
        c._id === chatId ? { ...c, hasUnread: false } : c,
      ),
    });
  }
}

function clearProjectUnreadForSharedChat(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
) {
  const projectQueries =
    queryClient.getQueriesData<ListAskPunttProjectsResponse>({
      queryKey: ["ask-puntt-projects"],
    });
  for (const [key, value] of projectQueries) {
    if (!value?.projects) continue;
    queryClient.setQueryData<ListAskPunttProjectsResponse>(key, {
      ...value,
      projects: value.projects.map((p) =>
        p.sharedChatId === chatId ? { ...p, hasUnread: false } : p,
      ),
    });
  }
}

function clearChatUnreadInProjectDetail(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: string,
) {
  const projectDetailQueries =
    queryClient.getQueriesData<GetAskPunttProjectResponse>({
      queryKey: ["ask-puntt-projects"],
    });
  for (const [key, value] of projectDetailQueries) {
    if (!value?.chats) continue;
    queryClient.setQueryData<GetAskPunttProjectResponse>(key, {
      ...value,
      chats: value.chats.map((c) =>
        c._id === chatId ? { ...c, hasUnread: false } : c,
      ),
    });
  }
}

/**
 * Mark chat messages read for the current user. Optimistically clears
 * hasUnread on cached chat/project lists and updates userSeen on history.
 */
export function useMarkAskPunttChatRead(chatId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload?: { messageId?: string }) =>
      AskPunttService.markChatRead(chatId!, payload),
    onMutate: async (payload) => {
      if (!chatId) return;
      const historyKey = askPunttKeys.messages(chatId);
      const previousHistory =
        queryClient.getQueryData<GetAskPunttChatHistoryResponse>(historyKey);

      queryClient.setQueryData<GetAskPunttChatHistoryResponse>(
        historyKey,
        (old) => {
          if (!old) return old;
          const readAt = new Date().toISOString();
          return {
            ...old,
            userSeen: {
              lastReadAt: readAt,
              unseenArtifactKeys: old.userSeen?.unseenArtifactKeys ?? [],
            },
          };
        },
      );
      clearChatUnreadInLists(queryClient, chatId);
      clearProjectUnreadForSharedChat(queryClient, chatId);
      clearChatUnreadInProjectDetail(queryClient, chatId);

      return { previousHistory, historyKey, messageId: payload?.messageId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousHistory !== undefined) {
        queryClient.setQueryData(context.historyKey, context.previousHistory);
      }
    },
    onSettled: () => {
      if (!chatId) return;
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.allChatsPrefix });
      queryClient.invalidateQueries({ queryKey: ["ask-puntt-projects"] });
    },
  });
}

/**
 * Mark a canvas or review artifact as seen for the current user.
 */
export function useMarkAskPunttArtifactSeen(chatId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AskPunttService.markArtifactSeen,
    onMutate: async (payload) => {
      if (!chatId) return;
      const historyKey = askPunttKeys.messages(chatId);
      const previousHistory =
        queryClient.getQueryData<GetAskPunttChatHistoryResponse>(historyKey);
      const artifactKey =
        payload.kind === "canvas"
          ? `canvas:${payload.messageId ?? ""}`
          : `review:${payload.ticketId ?? ""}`;

      queryClient.setQueryData<GetAskPunttChatHistoryResponse>(
        historyKey,
        (old) => {
          if (!old?.userSeen) return old;
          const unseen = new Set(old.userSeen.unseenArtifactKeys);
          unseen.delete(artifactKey);
          return {
            ...old,
            userSeen: {
              ...old.userSeen,
              unseenArtifactKeys: Array.from(unseen),
            },
          };
        },
      );

      return { previousHistory, historyKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousHistory !== undefined) {
        queryClient.setQueryData(context.historyKey, context.previousHistory);
      }
    },
    onSettled: () => {
      if (!chatId) return;
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
      invalidateAskPunttUnreadLists(queryClient);
    },
  });
}
